import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { AppConfig, JWT } from '../../config';
import { CompanyAction } from '../../actions';
import { Translate as t } from '../../config/Translate';
import { Loading } from '../../components/common/Loading';
import { SettingsReturnButton } from '../user/AdminSettingShortcuts';

const TEMPLATE_VARS = '{ticketKey} {summary} {createdAt} {link}';

const LEVELS = [
	{ key: 'Level1', label: 'Lv1', desc: 'Priority not set' },
	{ key: 'Level2', label: 'Lv2', desc: 'Assignee not set' },
	{ key: 'Level3', label: 'Lv3', desc: 'No response' },
];

const NOTI_FIELDS = [
	'notiSettingLevel1Enabled', 'notiSettingLevel1Email', 'notiSettingLevel1InApp',
	'notiSettingLevel2Enabled', 'notiSettingLevel2Email', 'notiSettingLevel2InApp',
	'notiSettingLevel3Enabled', 'notiSettingLevel3Email', 'notiSettingLevel3InApp',
	'notiSettingLevel1EmailSubject', 'notiSettingLevel1EmailTemplate',
	'notiSettingLevel2EmailSubject', 'notiSettingLevel2EmailTemplate',
	'notiSettingLevel3EmailSubject', 'notiSettingLevel3EmailTemplate',
];

const AdminNotificationEmailSetting = ({ company, GetCompanyInfo }) => {
	const userInfo = JSON.parse(localStorage.getItem('user') || '{}');

	const [branchUsers, setBranchUsers] = useState([]);
	const [isLoadingUsers, setIsLoadingUsers] = useState(true);
	const [emailSettings, setEmailSettings] = useState({
		notiSettingLevel1Enabled: false,
		notiSettingLevel1Email: false,
		notiSettingLevel1InApp: true,
		notiSettingLevel2Enabled: false,
		notiSettingLevel2Email: false,
		notiSettingLevel2InApp: true,
		notiSettingLevel3Enabled: false,
		notiSettingLevel3Email: false,
		notiSettingLevel3InApp: true,
		notiSettingLevel1EmailSubject: '',
		notiSettingLevel1EmailTemplate: '',
		notiSettingLevel2EmailSubject: '',
		notiSettingLevel2EmailTemplate: '',
		notiSettingLevel3EmailSubject: '',
		notiSettingLevel3EmailTemplate: '',
	});
	const [toast, setToast] = useState(null);
	const [modalLevel, setModalLevel] = useState(null);
	const [modalDraft, setModalDraft] = useState({ subject: '', template: '' });

	useEffect(() => {
		GetCompanyInfo(userInfo.companyId);
		loadBranchUsers();
	}, []);

	useEffect(() => {
		if (company && company.companyId && company.companyId !== 'ADD') {
			setEmailSettings({
				notiSettingLevel1Enabled: company.notiSettingLevel1Enabled || false,
				notiSettingLevel1Email: company.notiSettingLevel1Email || false,
				notiSettingLevel1InApp: company.notiSettingLevel1InApp !== false,
				notiSettingLevel2Enabled: company.notiSettingLevel2Enabled || false,
				notiSettingLevel2Email: company.notiSettingLevel2Email || false,
				notiSettingLevel2InApp: company.notiSettingLevel2InApp !== false,
				notiSettingLevel3Enabled: company.notiSettingLevel3Enabled || false,
				notiSettingLevel3Email: company.notiSettingLevel3Email || false,
				notiSettingLevel3InApp: company.notiSettingLevel3InApp !== false,
				notiSettingLevel1EmailSubject: company.notiSettingLevel1EmailSubject || '',
				notiSettingLevel1EmailTemplate: company.notiSettingLevel1EmailTemplate || '',
				notiSettingLevel2EmailSubject: company.notiSettingLevel2EmailSubject || '',
				notiSettingLevel2EmailTemplate: company.notiSettingLevel2EmailTemplate || '',
				notiSettingLevel3EmailSubject: company.notiSettingLevel3EmailSubject || '',
				notiSettingLevel3EmailTemplate: company.notiSettingLevel3EmailTemplate || '',
			});
		}
	}, [company]);

	const loadBranchUsers = async () => {
		setIsLoadingUsers(true);
		try {
			const ret = await axios.post(
				AppConfig.API_SERVER_PATH + '/user',
				{ type: 'branchUsers' },
				JWT()
			);
			if (ret.data.status === 'success') {
				// IMP-340: 사용자 목록 name(이름)순 정렬.
				setBranchUsers([...(ret.data.data || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
			}
		} catch (error) {
			console.error('Failed to load branch users', error);
		}
		setIsLoadingUsers(false);
	};

	const setEmailSetting = (key, val) => {
		setEmailSettings(prev => ({ ...prev, [key]: val }));
	};

	const saveEmailSettings = async () => {
		try {
			const ret = await axios.post(
				AppConfig.API_SERVER_PATH + '/company/' + company.companyId,
				{
					who: userInfo.adminid,
					type: 'update',
					data: Object.fromEntries(NOTI_FIELDS.map(f => [f, emailSettings[f]])),
				},
				JWT()
			);
			if (ret.data.status === 'success') {
				showToast('success', t('notification.message.saveSuccess'));
				GetCompanyInfo(company.companyId);
			} else {
				console.warn('[AdminNotificationEmailSetting] saveEmailSettings blocked', {
					handler: 'saveEmailSettings',
					companyId: company.companyId,
					reason: ret.data.err || 'API returned non-success status',
				});
				showToast('error', t('notification.message.saveFailed'));
			}
		} catch (error) {
			console.warn('[AdminNotificationEmailSetting] saveEmailSettings failed', {
				handler: 'saveEmailSettings',
				companyId: company.companyId,
				reason: error?.message || 'axios error',
			});
			showToast('error', t('notification.message.saveFailed'));
		}
	};

	const showToast = (type, message) => {
		setToast({ type, message });
		setTimeout(() => setToast(null), 3000);
	};

	const openModal = (levelKey) => {
		setModalLevel(levelKey);
		setModalDraft({
			subject: emailSettings[`notiSetting${levelKey}EmailSubject`] || '',
			template: emailSettings[`notiSetting${levelKey}EmailTemplate`] || '',
		});
	};

	const handleModalSave = () => {
		setEmailSetting(`notiSetting${modalLevel}EmailSubject`, modalDraft.subject);
		setEmailSetting(`notiSetting${modalLevel}EmailTemplate`, modalDraft.template);
		setModalLevel(null);
	};

	const handleUserToggle = async (adminid, field, currentVal) => {
		const newVal = !currentVal;
		setBranchUsers(prev =>
			prev.map(u => u.adminid === adminid ? { ...u, [field]: newVal } : u)
		);
		try {
			const ret = await axios.post(
				AppConfig.API_SERVER_PATH + '/user/' + adminid,
				{ who: userInfo.adminid, type: 'update', data: { [field]: newVal } },
				JWT()
			);
			if (ret.data.status !== 'success') {
				setBranchUsers(prev =>
					prev.map(u => u.adminid === adminid ? { ...u, [field]: currentVal } : u)
				);
				showToast('error', t('notification.message.saveFailed'));
			} else {
				showToast('success', t('notification.message.saveSuccess'));
			}
		} catch (error) {
			setBranchUsers(prev =>
				prev.map(u => u.adminid === adminid ? { ...u, [field]: currentVal } : u)
			);
			showToast('error', t('notification.message.saveFailed'));
		}
	};

	const renderToggle = (isOn, onClick) => (
		<div style={{ display: 'inline-block', cursor: 'pointer' }} onClick={onClick}>
			<div style={{
				width: 44, height: 24, borderRadius: 12,
				backgroundColor: isOn ? '#00beff' : '#ccc',
				position: 'relative', transition: 'background-color 0.2s',
			}}>
				<div style={{
					width: 20, height: 20, borderRadius: '50%',
					backgroundColor: '#fff', position: 'absolute', top: 2,
					left: isOn ? 22 : 2, transition: 'left 0.2s',
					boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
				}} />
			</div>
		</div>
	);

	const isCompanyLoaded = company && company.companyId && company.companyId !== 'ADD';
	const currentLevelInfo = modalLevel ? LEVELS.find(lv => lv.key === modalLevel) : null;

	return (
		<>
			<div className="extraHeader">
				<h2 className="mb-0">{t('notification.label.emailSetting')}</h2>
				<div className="ray-right">
					<SettingsReturnButton />
					<Link to="/Admin/AdminServiceStatus" className="btn btn-default btn-sm mt-0 me-1">
						← Service Status
					</Link>
				</div>
			</div>
			<div id="appCapsule" className="extra-header-active full-height">
				<div className="section mt-2">

					{/* Toast */}
					{toast && (
						<div className={`alert alert-${toast.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-3`} role="alert">
							{toast.message}
							<button type="button" className="btn-close" onClick={() => setToast(null)}></button>
						</div>
					)}

					{/* Email Template (Company-level) */}
					<div className="card mb-3">
						<div className="card-header fw-bold">{t('notification.label.emailSetting')}</div>
						{!isCompanyLoaded ? (
							<div className="card-body text-center py-4">
								<Loading />
							</div>
						) : (
							<>
								<div className="card-body p-0">
									<table className="table table-bordered mb-0 align-middle">
										<thead className="table-light">
											<tr>
												<th style={{ width: '20%' }}></th>
												{LEVELS.map(lv => (
													<th key={lv.key} className="text-center">
														{lv.label}
														<div className="fw-normal text-muted" style={{ fontSize: '0.8em' }}>{lv.desc}</div>
													</th>
												))}
											</tr>
										</thead>
										<tbody>
											<tr>
												<td className="fw-semibold">{t('notification.label.notifications')}</td>
												{LEVELS.map(lv => (
													<td key={lv.key} className="text-center">
														{renderToggle(
															emailSettings[`notiSetting${lv.key}Enabled`],
															() => setEmailSetting(`notiSetting${lv.key}Enabled`, !emailSettings[`notiSetting${lv.key}Enabled`])
														)}
													</td>
												))}
											</tr>
											<tr>
												<td style={{ paddingLeft: '1.75rem' }}>
													<span className="text-muted" style={{ fontSize: '0.85em' }}>└ {t('notification.label.emailChannel')}</span>
												</td>
												{LEVELS.map(lv => {
													const enabled = emailSettings[`notiSetting${lv.key}Enabled`];
													return (
														<td key={lv.key} className="text-center">
															<div style={!enabled ? { opacity: 0.35, pointerEvents: 'none' } : {}}>
																{renderToggle(
																	emailSettings[`notiSetting${lv.key}Email`],
																	() => setEmailSetting(`notiSetting${lv.key}Email`, !emailSettings[`notiSetting${lv.key}Email`])
																)}
															</div>
														</td>
													);
												})}
											</tr>
											<tr>
												<td style={{ paddingLeft: '1.75rem' }}>
													<span className="text-muted" style={{ fontSize: '0.85em' }}>└ {t('notification.label.inAppChannel')}</span>
												</td>
												{LEVELS.map(lv => {
													const enabled = emailSettings[`notiSetting${lv.key}Enabled`];
													return (
														<td key={lv.key} className="text-center">
															<div style={!enabled ? { opacity: 0.35, pointerEvents: 'none' } : {}}>
																{renderToggle(
																	emailSettings[`notiSetting${lv.key}InApp`],
																	() => setEmailSetting(`notiSetting${lv.key}InApp`, !emailSettings[`notiSetting${lv.key}InApp`])
																)}
															</div>
														</td>
													);
												})}
											</tr>
											<tr>
												<td>
													<div className="fw-semibold text-muted">Email Template</div>
													<small className="text-muted">{TEMPLATE_VARS}</small>
												</td>
												{LEVELS.map(lv => (
													<td key={lv.key} className="text-center">
														<button
															className="btn btn-outline-secondary btn-sm"
															onClick={() => openModal(lv.key)}
														>
															Edit
														</button>
													</td>
												))}
											</tr>
										</tbody>
									</table>
								</div>
								<div className="card-footer text-end">
									<button className="btn btn-primary btn-sm" onClick={saveEmailSettings}>
										Save Email Settings
									</button>
								</div>
							</>
						)}
					</div>

					{/* User Email Reception (User-level) */}
					<div className="card">
						<div className="card-header fw-bold">{t('notification.label.userEmailReception')}</div>
						<div className="card-body p-0">
							{isLoadingUsers ? (
								<div className="text-center py-4">
									<Loading />
								</div>
							) : (
								<table className="table table-bordered mb-0 align-middle">
									<thead className="table-light">
										<tr>
											<th rowSpan={2} className="align-middle">Name</th>
											<th rowSpan={2} className="align-middle">Email</th>
											<th rowSpan={2} className="align-middle">Company</th>
											<th rowSpan={2} className="align-middle text-center" style={{ width: '100px' }}>Receive Email</th>
											<th colSpan={3} className="text-center">{t('notification.label.emailReception')}</th>
										</tr>
										<tr>
											<th className="text-center" style={{ width: '80px' }}>Lv1</th>
											<th className="text-center" style={{ width: '80px' }}>Lv2</th>
											<th className="text-center" style={{ width: '80px' }}>Lv3</th>
										</tr>
									</thead>
									<tbody>
										{branchUsers.length === 0 ? (
											<tr>
												<td colSpan={7} className="text-center text-muted py-3">
													No managed users found.
												</td>
											</tr>
										) : (
											branchUsers.map(user => (
												<tr key={user.adminid} style={!user.isEmail ? { opacity: 0.5 } : {}}>
													<td>{user.name}</td>
													<td>{user.email}</td>
													<td>{user.companyName}</td>
													<td className="text-center">
														<span className={`badge ${user.isEmail ? 'bg-success' : 'bg-secondary'}`}>
															{user.isEmail ? 'ON' : 'OFF'}
														</span>
													</td>
													<td className="text-center">
														{renderToggle(user.notiEmailLevel1, () => handleUserToggle(user.adminid, 'notiEmailLevel1', user.notiEmailLevel1))}
													</td>
													<td className="text-center">
														{renderToggle(user.notiEmailLevel2, () => handleUserToggle(user.adminid, 'notiEmailLevel2', user.notiEmailLevel2))}
													</td>
													<td className="text-center">
														{renderToggle(user.notiEmailLevel3, () => handleUserToggle(user.adminid, 'notiEmailLevel3', user.notiEmailLevel3))}
													</td>
												</tr>
											))
										)}
									</tbody>
								</table>
							)}
						</div>
					</div>

				</div>
			</div>

			{/* Email Template Edit Modal */}
			<Modal show={!!modalLevel} onHide={() => setModalLevel(null)}>
				<Modal.Header>
					<Modal.Title>
						{currentLevelInfo ? `${currentLevelInfo.label} Email Template` : 'Email Template'}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="mb-3">
						<label className="form-label fw-semibold">Email Title</label>
						<input
							type="text"
							className="form-control"
							value={modalDraft.subject}
							onChange={e => setModalDraft(prev => ({ ...prev, subject: e.target.value }))}
							placeholder="Email subject line"
						/>
					</div>
					<div className="mb-2">
						<label className="form-label fw-semibold">Email Body</label>
						<small className="text-muted d-block mb-1">{TEMPLATE_VARS}</small>
						<textarea
							className="form-control"
							rows={6}
							value={modalDraft.template}
							onChange={e => setModalDraft(prev => ({ ...prev, template: e.target.value }))}
							placeholder="Default HTML template will be used if empty"
						/>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button className="btn btn-secondary" onClick={() => setModalLevel(null)}>
						{t('common.button.cancel') || 'Cancel'}
					</button>
					<button className="btn btn-primary" onClick={handleModalSave}>
						{t('common.button.save') || 'Save'}
					</button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

const mapState = (state) => ({
	company: state.CompanyReducer.company,
});

const mapDispatch = dispatch => ({
	GetCompanyInfo: (companyId) => dispatch(CompanyAction.GetCompanyInfo(companyId)),
});

export default connect(mapState, mapDispatch)(AdminNotificationEmailSetting);
